import type {
  AbstractMesh,
  EventState,
  Matrix,
  Mesh,
  SubMesh,
  Camera,
  Engine,
  Scene,
} from '@babylonjs/core';
import { Color4, ShaderMaterial, RawTexture, Effect } from '@babylonjs/core';

import { PointSizeType } from '../enums';
import { byLevelAndIndex } from '../utils/utils';

import potreeFragmentShader from './shader/potree.fragment.fx';
import potreeVertexShader from './shader/potree.vertex.fx';

import type { PointCloudOctree } from '../point-cloud-octree';
import type { PointCloudOctreeNode } from '../point-cloud-octree-node';

const ATTRIBUTES = ['position', 'uv', 'color'];

const UNIFORMS = [
  'worldViewProjection',
  'world',
  'view',
  'projection',
  'size',
  'octreeSize',
  'level',
  'vnStart',
  'screenWidth',
  'screenHeight',
  'fov',
  'spacing',
  'visibleNodes',
];

const SAMPLERS = ['visibleNodes'];

const POINT_SIZE_TYPE_DEFS = {
  [PointSizeType.FIXED]: 'POINT_SIZE_FIXED',
  [PointSizeType.ADAPTIVE]: 'POINT_SIZE_ADAPTIVE',
  [PointSizeType.ADAPTIVE_BY_DISTANCE]: 'POINT_SIZE_ADAPTIVE_BY_DISTANCE',
};

export interface IPointCloudMaterialParameters {
  size: number;
  minSize: number;
  maxSize: number;
  pointSizeType: PointSizeType;
}

const DEFAULT_PARAMETERS: IPointCloudMaterialParameters = {
  size: 1.0,
  minSize: 2.0,
  maxSize: 10.0,
  pointSizeType: PointSizeType.ADAPTIVE_BY_DISTANCE,
};

const VISIBLE_NODES_TEXTURE_SIZE = 2048;

export class PointCloudMaterial extends ShaderMaterial {
  visibleNodesTexture: RawTexture | undefined;

  public size: number = DEFAULT_PARAMETERS.size;
  public minSize: number = DEFAULT_PARAMETERS.minSize;
  public maxSize: number = DEFAULT_PARAMETERS.maxSize;
  public pointSizeType: PointSizeType = DEFAULT_PARAMETERS.pointSizeType;

  private _defines = [POINT_SIZE_TYPE_DEFS[this.pointSizeType]];

  private visibleNodesTextureData = new Uint8Array(
    VISIBLE_NODES_TEXTURE_SIZE * 4,
  );

  private visibleNodeTextureOffsets = new Map<string, number>();

  constructor(
    name: string,
    scene: Scene,
    parameters?: IPointCloudMaterialParameters,
  ) {
    Effect.ShadersStore.potreeVertexShader = potreeVertexShader;
    Effect.ShadersStore.potreeFragmentShader = potreeFragmentShader;

    const shaderPath = {
      vertex: 'potree',
      fragment: 'potree',
    };
    const options = {
      attributes: ATTRIBUTES,
      uniforms: UNIFORMS,
      samplers: SAMPLERS,
    };
    super(name, scene, shaderPath, options);
    this.options.defines = this._defines;
    this.pointsCloud = true;

    if (parameters) {
      this.size = parameters.size ?? DEFAULT_PARAMETERS.size;
      this.minSize = parameters.minSize ?? DEFAULT_PARAMETERS.minSize;
      this.maxSize = parameters.maxSize ?? DEFAULT_PARAMETERS.maxSize;
      this.pointSizeType =
        parameters.pointSizeType ?? DEFAULT_PARAMETERS.pointSizeType;
    }

    this._updateParameterUniformsAndDefines();

    this.setFloat('level', 0.0);
    this.setFloat('vnStart', 0.0);

    // const color = new Color4(1.0, 1.0, 1.0, 1.0);
    const color = new Color4(0.0, 0.0, 0.0, 1.0);
    const r = Math.floor(color.r * 255);
    const g = Math.floor(color.g * 255);
    const b = Math.floor(color.b * 255);

    for (let i = 0; i < VISIBLE_NODES_TEXTURE_SIZE; i++) {
      this.visibleNodesTextureData[i * 3] = r;
      this.visibleNodesTextureData[i * 3 + 1] = g;
      this.visibleNodesTextureData[i * 3 + 2] = b;
    }

    this.visibleNodesTexture = RawTexture.CreateRGBATexture(
      this.visibleNodesTextureData,
      VISIBLE_NODES_TEXTURE_SIZE,
      1,
      this.getScene(),
    );
    this.setTexture('visibleNodes', this.visibleNodesTexture);
  }

  private _updateParameterUniformsAndDefines() {
    this.setFloat('size', this.size);
    this.setFloat('minSize', this.minSize);
    this.setFloat('maxSize', this.maxSize);
    this._updateMutuallyExclusiveDefines(
      POINT_SIZE_TYPE_DEFS,
      this.pointSizeType,
    );
  }

  /**
   * Updates shader defines where only one value should be true at a time
   * @param defineMap Object containing all possible define values
   * @param currentValue The current value to set as true
   */
  private _updateMutuallyExclusiveDefines<T extends number | string>(
    defineMap: { [key in T]: string },
    currentValue: T,
  ) {
    // Set all defines to false first
    (Object.values(defineMap) as string[]).forEach((define) => {
      this.setDefine(define, false);
    });
    // Set only the current define to true
    this.setDefine(defineMap[currentValue], true);
  }

  public override bindForSubMesh(world: Matrix, mesh: Mesh, subMesh: SubMesh) {
    super.bindForSubMesh(world, mesh, subMesh);
    this._updateParameterUniformsAndDefines();
    this._afterBind(mesh, this._activeEffect, subMesh);
  }

  public update(
    octree: PointCloudOctree,
    visibleNodes: PointCloudOctreeNode[],
    camera: Camera,
    engine: Engine,
  ) {
    const effect = this.getEffect();
    effect?.setFloat('fov', camera.fov);
    effect?.setFloat('screenWidth', engine.getRenderWidth(true));
    effect?.setFloat('screenHeight', engine.getRenderHeight(true));

    const octreeSize =
      octree.pcoGeometry.boundingBox.max.x -
      octree.pcoGeometry.boundingBox.min.x;
    effect?.setFloat('octreeSize', octreeSize);

    const maxScale = Math.max(
      octree.scaling.x,
      octree.scaling.y,
      octree.scaling.z,
    );
    effect?.setFloat('spacing', octree.pcoGeometry.spacing * maxScale);

    this.updateVisibilityTextureData(visibleNodes);
  }

  private updateVisibilityTextureData(nodes: PointCloudOctreeNode[]) {
    nodes.sort(byLevelAndIndex);

    this.visibleNodesTextureData.fill(0);
    const offsetsToChild = new Array(nodes.length).fill(Infinity);

    this.visibleNodeTextureOffsets.clear();

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      this.visibleNodeTextureOffsets.set(node.name, i);

      if (i > 0) {
        const parentName = node.name.slice(0, -1);
        const parentOffset = this.visibleNodeTextureOffsets.get(parentName)!;
        const parentOffsetToChild = i - parentOffset;

        offsetsToChild[parentOffset] = Math.min(
          offsetsToChild[parentOffset],
          parentOffsetToChild,
        );

        // tslint:disable:no-bitwise
        const offset = parentOffset * 4;
        this.visibleNodesTextureData[offset] =
          this.visibleNodesTextureData[offset] | (1 << node.index);
        this.visibleNodesTextureData[offset + 1] =
          offsetsToChild[parentOffset] >> 8;
        this.visibleNodesTextureData[offset + 2] =
          offsetsToChild[parentOffset] % 256;
        // tslint:enable:no-bitwise
      }

      this.visibleNodesTextureData[i * 4 + 3] = node.name.length;
    }

    if (this.visibleNodesTexture) {
      this.visibleNodesTexture?.update(this.visibleNodesTextureData);
      // this.setTexture("visibleNodes", this.visibleNodesTexture);
      // this.getEffect()?.setTexture("visibleNodes", texture);
    }
  }

  // static makeOnBeforeRender(
  // 	octree: PointCloudOctree,
  // 	node: PointCloudOctreeNode,
  // 	pcIndex?: number,
  // ) {
  //   return (
  // 		mesh: AbstractMesh,
  //     state: EventState
  // 	) =>
  // 	{
  //     const material = mesh.material as PointCloudMaterial;
  //     const effect = material.getEffect();
  //     // console.log("EFFECT ID = ", effect?.);
  // 		if (material instanceof PointCloudMaterial && effect) {

  //       // console.log("ACTIVE EFFECT = ", material?._uniformBuffer);

  //       // material.setFloat("level", node.level);
  // 			// material.setInt("isLeafNode", node.isLeafNode ? 1 : 0);
  //       // console.log("node.name = ", node.name, " node.level = ", node.level);
  // 			effect?.setFloat("level", node.level);
  // 			// effect?.setInt("isLeafNode", node.isLeafNode ? 1 : 0);

  // 			const vnStart = material.visibleNodeTextureOffsets.get(node.name);
  //       if (node.name.startsWith('r064')) {
  //         // console.log("NODE NAME = ", node.name, " VNSTART = ", vnStart);
  //       }
  // 			if (vnStart !== undefined)
  // 			{
  //         // console.log("vnStart = ", vnStart);
  // 				effect?.setFloat("vnStart", vnStart);
  //         // effect?.setFloat("vnStart", 30.0);
  //         // effect?.setFloat("vnStart", 30.0);
  // 				// material.setFloat("vnStart", vnStart);
  // 			} else {
  //         console.log("vnStart is undefined");
  //       }

  // 			// materialUniforms.pcIndex.value =
  // 			// pcIndex !== undefined ? pcIndex : octree.visibleNodes.indexOf(node);

  // 			// Remove the cast to any after updating to Three.JS >= r113
  // 			// (material as RawShaderMaterial).uniformsNeedUpdate = true;
  // 		}
  // 	};
  // };

  private _isVnStartBindingSetup = false;

  static makeOnBeforeRender(
    octree: PointCloudOctree,
    node: PointCloudOctreeNode,
    pcIndex?: number,
  ) {
    return (mesh: AbstractMesh, state: EventState) => {
      const material = mesh.material as PointCloudMaterial;
      if (material instanceof PointCloudMaterial) {
        // Store mesh-specific data in the material's custom buffer
        if (!mesh.metadata) mesh.metadata = {};
        mesh.metadata.vnStart = material.visibleNodeTextureOffsets.get(
          node.name,
        );
        mesh.metadata.level = node.level;
        // Update the Effect in the material's onBindObservable
        if (!material._isVnStartBindingSetup) {
          material._isVnStartBindingSetup = true;
          material.onBindObservable.add((mesh: AbstractMesh) => {
            const currentMesh = mesh;
            if (
              currentMesh &&
              currentMesh.metadata &&
              currentMesh.metadata.vnStart !== undefined
            ) {
              material
                .getEffect()
                ?.setFloat('vnStart', currentMesh.metadata.vnStart);
            }
            if (
              currentMesh &&
              currentMesh.metadata &&
              currentMesh.metadata.level !== undefined
            ) {
              material
                .getEffect()
                ?.setFloat('level', currentMesh.metadata.level);
            }
          });
        }
      }
    };
  }
}
